/**
 * A linked group's consumption queue, in the EXACT order the planner will
 * walk it (shared list-order helpers — the engine uses the same rule).
 * MANUAL makes the rows draggable (not while filtered); FORMULA (Phase 18)
 * shows what the lists are ranked on — choosing Formula, or clicking that
 * line, opens the formula and its rules in the Flow Optimizer's window, the
 * queue live on the right (user, 12/10/2026: "it should open
 * automatically") — and each row's score, or why it is not ranked.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useMemo } from 'react';
import { Maximize2 } from 'lucide-react';
import { compareListsByDefaultOrder, describeHourFormula } from '@trafficpulse/shared';

import { useListGroupResolve } from '@/features/list-groups';

import type { DraftGroupRef } from '../../../drop-generator.types';
import { rulesSummary } from '../rules/rule-model';
import type { ContainerEditor } from '../state/useContainerEditor';
import { ListFormulaDialog, rankingStatus } from './formula/ListFormulaDialog';
import { queueOrder } from './formula/queue-order';
import { useListRanking } from './formula/use-list-ranking';
import { QueueList } from './queue/QueueList';

const CAPTION: Record<string, string> = {
  MANUAL: 'Drag rows into your order. New rule matches join at the back. The group keeps its list of the day until a verdict moves it on — this order decides where it goes next.',
  MANUAL_UNSET: 'Biggest list first until you drag a row into your own order; then new rule matches join at the back. The group keeps its list of the day until a verdict moves it on — this order decides where it goes next.',
  FORMULA: 'Best score first; a list with too little traffic keeps its biggest-first place after the ranked ones. The group keeps its list of the day until a verdict moves it on — this order decides where it goes next.',
  INHERIT: 'Biggest list first; depleted lists are skipped. The group keeps its list of the day until a verdict moves it on — this order decides where it goes next.',
};

/** "My order" with nothing dragged yet still reads biggest first. */
const captionKey = (link: DraftGroupRef) =>
  link.orderMode === 'MANUAL' && !link.manualOrderJson?.length ? 'MANUAL_UNSET' : link.orderMode;

interface Props {
  link: DraftGroupRef;
  group: { name: string; color: string };
  editor: ContainerEditor;
  formulaSource: { countryKey: string; campaignWiserId: number | null };
  onPatch: (patch: Partial<DraftGroupRef>) => void;
  /** The list formula window — opened by choosing Formula (GroupsTab) or clicking "Ranked on". */
  formulaOpen: boolean;
  onFormulaOpenChange: (open: boolean) => void;
}

export const GroupOrderPanel: FC<Props> = ({ link, group, editor, formulaSource, onPatch, formulaOpen, onFormulaOpenChange }) => {
  const resolve = useListGroupResolve(link.listGroupId);
  const formula = link.orderMode === 'FORMULA';
  const byDefault = useMemo(() => [...(resolve.data?.lists ?? [])].sort(compareListsByDefaultOrder), [resolve.data]);
  const ranking = useListRanking({ ...formulaSource, ranking: link.orderFormulaJson, wiserIds: byDefault.map((r) => r.wiserId), enabled: formula });
  const preview = formula ? ranking.data : undefined;
  const ordered = useMemo(() => queueOrder(byDefault, link, preview), [byDefault, link, preview]);

  if (resolve.isLoading) return <div className="h-16 animate-pulse rounded-lg bg-muted/60" />;
  if (resolve.isError)
    return (
      <p className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground">
        Queue preview unavailable.{' '}
        <button type="button" className="font-medium text-primary hover:underline" onClick={() => resolve.refetch()}>
          Retry
        </button>
      </p>
    );

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
      {formula ? (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-label="Edit the list formula"
          onClick={() => onFormulaOpenChange(true)}
          className="group flex w-full items-center gap-2 rounded-md border border-border/60 bg-card px-2.5 py-1.5 text-left text-xs text-muted-foreground transition-shadow hover:border-primary/40 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="min-w-0 flex-1">
            Ranked on <span className="text-foreground">{[describeHourFormula(link.orderFormulaJson.formula), rulesSummary(link.orderFormulaJson.rules)].filter(Boolean).join(' · ')}</span> — {rankingStatus(ranking)}
          </span>
          <Maximize2 className="h-3.5 w-3.5 shrink-0 transition-colors group-hover:text-primary" aria-hidden />
        </button>
      ) : null}
      <p className="text-[11px] text-muted-foreground">{CAPTION[captionKey(link)] ?? CAPTION.INHERIT}</p>
      <QueueList ordered={ordered} preview={preview} manual={link.orderMode === 'MANUAL'} onReorder={(manualOrderJson) => onPatch({ manualOrderJson })} />
      {formula ? (
        <ListFormulaDialog
          open={formulaOpen}
          onOpenChange={onFormulaOpenChange}
          editor={editor}
          group={group}
          ranking={link.orderFormulaJson}
          query={ranking}
          ordered={ordered}
          preview={preview}
          onChange={(r) => onPatch({ orderFormulaJson: r })}
        />
      ) : null}
    </div>
  );
};
